import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { fmtINR } from "@/lib/format";
import { toast } from "sonner";
import { Calculator, Download, Sparkles, Landmark, ReceiptText, TrendingUp, Scale } from "lucide-react";

export default function ITRAutoPrep() {
  const [fy, setFy] = useState("2025-26");
  const [form, setForm] = useState("ITR-6");
  const [detect, setDetect] = useState(null);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/itr/detect").then((r) => { setDetect(r.data); setForm(r.data.recommended || "ITR-6"); }); }, []);
  useEffect(() => { api.get("/itr/draft", { params: { fy } }).then((r) => setDraft(r.data.computation ? r.data : null)); }, [fy]);

  const compute = async () => {
    setBusy(true);
    try { const r = await api.post("/itr/draft", { fy, form }); setDraft(r.data); toast.success(`${form} draft computed for FY ${fy}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const dl = () => {
    if (!draft) return;
    const blob = new Blob([JSON.stringify(draft, null, 2)], { type: "application/json" });
    const u = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = u; a.download = `${form}_${fy}.json`; a.click(); URL.revokeObjectURL(u);
  };

  const bs = draft?.part_a_bs;
  const tds = draft?.schedule_tds;
  const cg = draft?.schedule_cg;
  const mat = draft?.mat;

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="itr-page">
      <PageHeader eyebrow="Compliance" title="ITR Auto-Prep · Direct Tax"
        description="Balance Sheet · P&L · Schedule TDS · Capital Gains · MAT — auto-computed from your ledger and exported as CBDT-utility JSON." />

      {detect && (
        <Card className="p-5 rounded-sm border-2 border-accent/40 bg-accent/5">
          <div className="flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="text-[10px] font-mono uppercase tracking-widest text-accent">Recommended</div>
              <div className="text-2xl font-heading font-bold mt-1">{detect.recommended}</div>
              <div className="text-sm text-muted-foreground mt-1">
                Your constitution: <b className="text-foreground">{detect.entity_type || "unspecified"}</b> · {detect.reason}
              </div>
            </div>
          </div>
        </Card>
      )}

      <Card className="p-4 rounded-sm flex items-end gap-3 flex-wrap">
        <div><Label>Financial Year</Label><Input value={fy} onChange={(e) => setFy(e.target.value)} className="rounded-sm mt-1 w-40" data-testid="itr-fy" /></div>
        <div><Label>ITR Form</Label>
          <Select value={form} onValueChange={setForm}>
            <SelectTrigger className="rounded-sm mt-1 w-40" data-testid="itr-form"><SelectValue /></SelectTrigger>
            <SelectContent>{(detect?.all_forms || []).map((f) => <SelectItem key={f.code} value={f.code}>{f.code}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="flex-1" />
        <Button onClick={compute} disabled={busy} className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5" data-testid="itr-compute"><Calculator className="w-4 h-4" /> {busy ? "Computing…" : "Compute Draft"}</Button>
        {draft && <Button variant="outline" onClick={dl} className="rounded-sm gap-1.5" data-testid="itr-dl"><Download className="w-4 h-4" /> Download JSON</Button>}
      </Card>

      {draft?.computation && (
        <Tabs defaultValue="pnl" className="w-full">
          <TabsList className="grid grid-cols-5 w-full rounded-sm bg-muted/50 h-auto p-1">
            <TabsTrigger value="pnl" className="rounded-sm text-xs data-[state=active]:bg-accent data-[state=active]:text-white gap-1.5" data-testid="itr-tab-pnl"><ReceiptText className="w-3.5 h-3.5" /> P&L</TabsTrigger>
            <TabsTrigger value="bs" className="rounded-sm text-xs data-[state=active]:bg-accent data-[state=active]:text-white gap-1.5" data-testid="itr-tab-bs"><Landmark className="w-3.5 h-3.5" /> Balance Sheet</TabsTrigger>
            <TabsTrigger value="tds" className="rounded-sm text-xs data-[state=active]:bg-accent data-[state=active]:text-white gap-1.5" data-testid="itr-tab-tds"><Scale className="w-3.5 h-3.5" /> Schedule TDS</TabsTrigger>
            <TabsTrigger value="cg" className="rounded-sm text-xs data-[state=active]:bg-accent data-[state=active]:text-white gap-1.5" data-testid="itr-tab-cg"><TrendingUp className="w-3.5 h-3.5" /> Capital Gains</TabsTrigger>
            <TabsTrigger value="mat" className="rounded-sm text-xs data-[state=active]:bg-accent data-[state=active]:text-white gap-1.5" data-testid="itr-tab-mat"><Calculator className="w-3.5 h-3.5" /> MAT</TabsTrigger>
          </TabsList>

          {/* P&L + Tax */}
          <TabsContent value="pnl" className="mt-4">
            <div className="grid md:grid-cols-2 gap-4">
              <Card className="p-5 rounded-sm">
                <div className="text-[10px] font-mono uppercase tracking-widest text-accent">Part A · P&L (from ledger)</div>
                <table className="w-full mt-3 text-sm">
                  <tbody>
                    <tr><td className="text-muted-foreground py-1">Turnover</td><td className="text-right tabular-nums font-mono">{fmtINR(draft.part_a_pl.turnover)}</td></tr>
                    <tr><td className="text-muted-foreground py-1">Purchases</td><td className="text-right tabular-nums font-mono">{fmtINR(draft.part_a_pl.purchases)}</td></tr>
                    <tr><td className="text-muted-foreground py-1">Gross Profit</td><td className="text-right tabular-nums font-mono font-semibold">{fmtINR(draft.part_a_pl.gross_profit)}</td></tr>
                    <tr><td className="text-muted-foreground py-1">Depreciation (IT Act)</td><td className="text-right tabular-nums font-mono">{fmtINR(draft.part_a_pl.depreciation)}</td></tr>
                    <tr className="border-t"><td className="py-2 font-semibold">Profit Before Tax</td><td className="text-right tabular-nums font-bold">{fmtINR(draft.part_a_pl.profit_before_tax)}</td></tr>
                  </tbody>
                </table>
              </Card>
              <Card className="p-5 rounded-sm">
                <div className="text-[10px] font-mono uppercase tracking-widest text-accent">Computation of Tax</div>
                <table className="w-full mt-3 text-sm">
                  <tbody>
                    <tr><td className="text-muted-foreground py-1">Total Income</td><td className="text-right tabular-nums font-mono">{fmtINR(draft.computation.total_income)}</td></tr>
                    <tr><td className="text-muted-foreground py-1">Base tax @ {draft.computation.tax_rate_used_pct}%</td><td className="text-right tabular-nums font-mono">{fmtINR(draft.computation.tax_before_surcharge)}</td></tr>
                    <tr><td className="text-muted-foreground py-1">Surcharge</td><td className="text-right tabular-nums font-mono">{fmtINR(draft.computation.surcharge)}</td></tr>
                    <tr><td className="text-muted-foreground py-1">H&E Cess (4%)</td><td className="text-right tabular-nums font-mono">{fmtINR(draft.computation.cess_4pct)}</td></tr>
                    <tr className="border-t"><td className="py-1 font-semibold">Normal Tax</td><td className="text-right tabular-nums font-mono">{fmtINR(draft.computation.total_tax_liability)}</td></tr>
                    <tr><td className="text-muted-foreground py-1">MAT liability</td><td className="text-right tabular-nums font-mono">{fmtINR(draft.computation.mat_liability)}</td></tr>
                    <tr><td className="text-muted-foreground py-1">TDS credit utilised</td><td className="text-right tabular-nums font-mono text-emerald-700">- {fmtINR(draft.computation.tds_credit_utilised)}</td></tr>
                    <tr className="border-t bg-black text-white"><td className="py-2 px-2 font-bold">Net Tax Payable</td><td className="text-right px-2 tabular-nums font-bold">{fmtINR(draft.computation.net_payable)}</td></tr>
                  </tbody>
                </table>
              </Card>
            </div>
          </TabsContent>

          {/* Balance Sheet */}
          <TabsContent value="bs" className="mt-4">
            {bs && (
              <div className="grid md:grid-cols-2 gap-4">
                <Card className="p-5 rounded-sm">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-accent">Part A-BS · Sources of Funds</div>
                  <table className="w-full mt-3 text-sm">
                    <tbody>
                      <tr><td className="text-muted-foreground py-1">Share Capital</td><td className="text-right tabular-nums font-mono">{fmtINR(bs.sources_of_funds.share_capital)}</td></tr>
                      <tr><td className="text-muted-foreground py-1">Reserves & Surplus</td><td className="text-right tabular-nums font-mono">{fmtINR(bs.sources_of_funds.reserves_and_surplus)}</td></tr>
                      <tr><td className="text-muted-foreground py-1">Loans (Secured + Unsecured)</td><td className="text-right tabular-nums font-mono">{fmtINR(bs.sources_of_funds.loans)}</td></tr>
                      <tr className="border-t bg-muted/40"><td className="py-2 font-semibold">Total Sources</td><td className="text-right tabular-nums font-bold">{fmtINR(bs.sources_of_funds.total)}</td></tr>
                    </tbody>
                  </table>
                </Card>
                <Card className="p-5 rounded-sm">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-accent">Part A-BS · Application of Funds</div>
                  <table className="w-full mt-3 text-sm">
                    <tbody>
                      <tr><td className="text-muted-foreground py-1">Fixed Assets · Gross Block</td><td className="text-right tabular-nums font-mono">{fmtINR(bs.application_of_funds.fixed_assets_gross_block)}</td></tr>
                      <tr><td className="text-muted-foreground py-1">Less: Accumulated Depreciation</td><td className="text-right tabular-nums font-mono">- {fmtINR(bs.application_of_funds.fixed_assets_accum_dep)}</td></tr>
                      <tr><td className="py-1 font-semibold">Net Block</td><td className="text-right tabular-nums font-mono font-semibold">{fmtINR(bs.application_of_funds.fixed_assets_net_block)}</td></tr>
                      <tr><td className="text-muted-foreground py-1">Current Assets (Debtors / Bank / Stock)</td><td className="text-right tabular-nums font-mono">{fmtINR(bs.application_of_funds.current_assets)}</td></tr>
                      <tr className="border-t bg-muted/40"><td className="py-2 font-semibold">Total Application</td><td className="text-right tabular-nums font-bold">{fmtINR(bs.application_of_funds.total)}</td></tr>
                    </tbody>
                  </table>
                </Card>
                <Card className="p-3 rounded-sm md:col-span-2 bg-amber-50/50 text-xs text-muted-foreground italic">{bs.note}</Card>
              </div>
            )}
          </TabsContent>

          {/* Schedule TDS */}
          <TabsContent value="tds" className="mt-4">
            {tds && (
              <Card className="p-5 rounded-sm">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-accent">Schedule TDS · Section-wise</div>
                  <div className="text-sm">Total TDS credit: <b className="font-mono text-emerald-700">{fmtINR(tds.total_tds_credit)}</b></div>
                </div>
                {tds.by_section.length === 0 ? (
                  <div className="text-sm text-muted-foreground italic py-4">No TDS deducted for this FY. Verify with 26AS import.</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50 text-[10px] font-mono uppercase">
                      <tr><th className="text-left p-2">Section</th><th className="text-right p-2">Amount credited</th><th className="text-right p-2">TDS deducted</th><th className="text-right p-2">Deductions</th></tr>
                    </thead>
                    <tbody>
                      {tds.by_section.map((s) => (
                        <tr key={s.section} className="border-b" data-testid={`itr-tds-row-${s.section}`}>
                          <td className="p-2 font-mono">{s.section}</td>
                          <td className="p-2 text-right tabular-nums font-mono">{fmtINR(s.amount_credited)}</td>
                          <td className="p-2 text-right tabular-nums font-mono font-semibold text-emerald-700">{fmtINR(s.tds_deducted)}</td>
                          <td className="p-2 text-right tabular-nums font-mono">{s.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </Card>
            )}
          </TabsContent>

          {/* Capital Gains */}
          <TabsContent value="cg" className="mt-4">
            {cg && (
              <div className="space-y-4">
                <div className="grid md:grid-cols-3 gap-3">
                  <Card className="p-4 rounded-sm">
                    <div className="text-[10px] font-mono uppercase text-muted-foreground">STCG (u/s 111A · &lt; 24m)</div>
                    <div className="text-2xl font-bold font-mono mt-1">{fmtINR(cg.stcg_total)}</div>
                  </Card>
                  <Card className="p-4 rounded-sm">
                    <div className="text-[10px] font-mono uppercase text-muted-foreground">LTCG (u/s 112 · ≥ 24m)</div>
                    <div className="text-2xl font-bold font-mono mt-1">{fmtINR(cg.ltcg_total)}</div>
                  </Card>
                  <Card className="p-4 rounded-sm bg-accent/5">
                    <div className="text-[10px] font-mono uppercase text-accent">Total Capital Gains</div>
                    <div className="text-2xl font-bold font-mono mt-1 text-accent">{fmtINR(cg.total_cg)}</div>
                  </Card>
                </div>
                <Card className="p-5 rounded-sm">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-accent mb-2">Disposals in FY</div>
                  {cg.rows.length === 0 ? (
                    <div className="text-sm text-muted-foreground italic py-4">No fixed-asset disposals detected in FY {fy}.</div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50 text-[10px] font-mono uppercase">
                        <tr><th className="text-left p-2">Date</th><th className="text-left p-2">Asset</th><th className="text-right p-2">Sale price</th><th className="text-right p-2">WDV</th><th className="text-right p-2">Gain</th><th className="text-right p-2">Class</th></tr>
                      </thead>
                      <tbody>
                        {cg.rows.map((r, i) => (
                          <tr key={i} className="border-b">
                            <td className="p-2 font-mono">{r.date}</td>
                            <td className="p-2">{r.asset}</td>
                            <td className="p-2 text-right tabular-nums font-mono">{fmtINR(r.sale_price)}</td>
                            <td className="p-2 text-right tabular-nums font-mono">{fmtINR(r.wdv)}</td>
                            <td className="p-2 text-right tabular-nums font-mono font-semibold">{fmtINR(r.gain)}</td>
                            <td className="p-2 text-right"><span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm ${r.class === "LTCG" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{r.class}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  <div className="text-[11px] italic text-muted-foreground mt-3">{cg.note}</div>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* MAT */}
          <TabsContent value="mat" className="mt-4">
            {mat && (
              <Card className="p-5 rounded-sm">
                <div className="text-[10px] font-mono uppercase tracking-widest text-accent">Minimum Alternate Tax · {mat.applicable_section}</div>
                <table className="w-full mt-3 text-sm">
                  <tbody>
                    <tr><td className="text-muted-foreground py-1">Book Profit (as per Cos Act depreciation)</td><td className="text-right tabular-nums font-mono">{fmtINR(mat.book_profit)}</td></tr>
                    <tr><td className="text-muted-foreground py-1">MAT rate</td><td className="text-right tabular-nums font-mono">{mat.mat_rate_pct}%</td></tr>
                    <tr><td className="text-muted-foreground py-1">MAT before cess</td><td className="text-right tabular-nums font-mono">{fmtINR(mat.mat_before_cess)}</td></tr>
                    <tr><td className="text-muted-foreground py-1">H&E Cess (4%)</td><td className="text-right tabular-nums font-mono">{fmtINR(mat.cess_4pct)}</td></tr>
                    <tr className="border-t"><td className="py-1 font-semibold">MAT Liability</td><td className="text-right tabular-nums font-mono font-semibold">{fmtINR(mat.mat_liability)}</td></tr>
                    <tr><td className="text-muted-foreground py-1">Normal Tax Liability</td><td className="text-right tabular-nums font-mono">{fmtINR(mat.normal_tax_liability)}</td></tr>
                    <tr className="border-t bg-black text-white"><td className="py-2 px-2 font-bold">Tax Payable (higher of the two)</td><td className="text-right px-2 tabular-nums font-bold">{fmtINR(mat.tax_payable_higher_of_two)}</td></tr>
                    <tr><td className="text-emerald-700 py-1 font-semibold">MAT Credit c/f (u/s 115JAA)</td><td className="text-right tabular-nums font-mono text-emerald-700 font-semibold">{fmtINR(mat.mat_credit_c_f)}</td></tr>
                  </tbody>
                </table>
                <div className="text-[11px] italic text-muted-foreground mt-3">MAT applies only if normal tax &lt; 15% of book profit. Credit carried forward is available for set-off up to 15 years.</div>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      )}

      {detect && !draft?.computation && (
        <Card className="p-5 rounded-sm">
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-3">All ITR Forms</div>
          <div className="grid md:grid-cols-2 gap-2 text-sm">
            {detect.all_forms.map((f) => (
              <div key={f.code} className={`p-2 rounded-sm border ${f.code === form ? "border-accent bg-accent/10" : "border-border"}`}>
                <b className="font-mono">{f.code}</b> · <span className="text-muted-foreground text-xs">{f.for}</span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
